import { useDeploymentSource } from "@artis/sdk-react";
import { Button, ToastNotification } from "@artis/ui";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";

import {
  bottomRightToastOptions,
  dismissBottomRightToast,
  registerBottomRightToastDismiss,
} from "../components/bottomRightToastStack";
import { restartAllWindows } from "../../context/reload";
import { createUpdatePrompt, type UpdatePrompt, type UpdatePromptState } from "./updatePrompt";

const UPDATE_TOAST_ID = "new-update-available";

const UpdatePromptContext = createContext<UpdatePrompt | null>(null);

export const UpdatePromptProvider = ({ children }: { children: ReactNode }) => {
  const deployment = useDeploymentSource();
  const [prompt, setPrompt] = useState<UpdatePrompt | null>(null);
  useEffect(() => {
    if (!deployment) {
      setPrompt(null);
      return;
    }
    const next = createUpdatePrompt({ deployment, restart: restartAllWindows });
    setPrompt(next);
    return next.dispose;
  }, [deployment]);
  return <UpdatePromptContext.Provider value={prompt}>{children}</UpdatePromptContext.Provider>;
};

const useUpdatePromptState = (): UpdatePromptState => {
  const prompt = useContext(UpdatePromptContext);
  const subscribe = useCallback((cb: () => void) => prompt?.subscribe(cb) ?? (() => {}), [prompt]);
  const getSnapshot = useCallback(() => prompt?.get() ?? "hidden", [prompt]);
  return useSyncExternalStore(subscribe, getSnapshot);
};

export const UpdateToastGate = () => {
  const prompt = useContext(UpdatePromptContext);
  const state = useUpdatePromptState();

  useEffect(() => {
    if (!prompt || state !== "toast") {
      dismissBottomRightToast(UPDATE_TOAST_ID);
      return;
    }
    const dismiss = registerBottomRightToastDismiss(UPDATE_TOAST_ID, () => {
      prompt.dismiss();
      dismissBottomRightToast(UPDATE_TOAST_ID);
    });
    toast.custom((t) => <UpdateToast visible={t.visible} onDismiss={dismiss} onUpdate={prompt.update} />, {
      ...bottomRightToastOptions(UPDATE_TOAST_ID, { duration: Number.POSITIVE_INFINITY }),
    });
  }, [prompt, state]);

  return null;
};

const UpdateToast = ({
  visible,
  onDismiss,
  onUpdate,
}: {
  visible: boolean;
  onDismiss: () => void;
  onUpdate: () => void;
}) => (
  <ToastNotification
    title="New update available"
    data-testid="update-toast"
    visible={visible}
    onDismiss={onDismiss}
    actions={<Button onClick={onUpdate}>Reload</Button>}
  >
    Reload now for new and improved features to enhance your experience on Artis.
  </ToastNotification>
);

export const UpdateAvailableButton = () => {
  const prompt = useContext(UpdatePromptContext);
  const state = useUpdatePromptState();
  if (!prompt || state !== "button") return null;
  return (
    <Button size="sm" onClick={prompt.update} data-testid="update-available-button">
      Reload to Update
    </Button>
  );
};
